'use client';

import clsx from 'clsx';

import { useAriaLabels } from '@/hooks/useAriaLabels';
import { Link } from '@/lib/i18n/navigation';

import {
  buildBreadcrumbJsonLd,
  ChevronSeparator,
  formatLabel,
  SlashSeparator,
  type BreadcrumbsInlineProps,
} from './Breadcrumbs.shared';
import { BreadcrumbsExpandable } from './BreadcrumbsExpandable';
import styles from '../Breadcrumbs.module.scss';

function renderCtaItem(
  item: { label: string; href?: string },
  index: number,
  testId: string,
  isLast: boolean
) {
  const label = formatLabel(item.label);

  return (
    <>
      {item.href ? (
        <Link
          href={item.href}
          className={styles.linkCta}
          data-testid={index === 0 ? `${testId}-home` : `${testId}-link-${index}`}
        >
          {label}
        </Link>
      ) : (
        <span
          className={styles.currentCta}
          aria-current={isLast ? 'page' : undefined}
          data-testid={isLast ? `${testId}-current` : undefined}
        >
          {label}
        </span>
      )}
      <SlashSeparator />
    </>
  );
}

/**
 * Compact inline breadcrumb trail.
 * Default: chevron separators for PLPs/PDPs.
 * CTA: slash separators and green links for Gallet hero headers.
 */
export const BreadcrumbsInline = ({
  items,
  className,
  testId = 'Breadcrumbs',
  includeJsonLd = true,
  appearance = 'default',
}: BreadcrumbsInlineProps) => {
  const aria = useAriaLabels();
  const isCta = appearance === 'cta';

  if (!items?.length) {
    return null;
  }

  if (items.length >= 4 && !isCta) {
    return (
      <BreadcrumbsExpandable
        items={items}
        className={className}
        testId={testId}
        includeJsonLd={includeJsonLd}
      />
    );
  }

  const jsonLd = buildBreadcrumbJsonLd(items);

  return (
    <nav
      className={clsx(styles.wrapper, isCta && styles.wrapperCta, className)}
      aria-label={aria.action('breadcrumb')}
      data-testid={testId}
    >
      <ol className={clsx(styles.list, isCta && styles.listCta)}>
        {items.map((item, index) => (
          <li
            key={`${item.label}-${index}`}
            className={clsx(styles.item, isCta && styles.itemCta)}
            data-testid={`${testId}-item-${index}`}
          >
            {isCta ? (
              renderCtaItem(item, index, testId, index === items.length - 1)
            ) : (
              <>
                {index > 0 && <ChevronSeparator />}
                {!item.href ? (
                  <span
                    className={styles.current}
                    aria-current="page"
                    data-testid={`${testId}-current`}
                  >
                    {formatLabel(item.label)}
                  </span>
                ) : (
                  <Link
                    href={item.href}
                    className={styles.link}
                    data-testid={`${testId}-link-${index}`}
                  >
                    {formatLabel(item.label)}
                  </Link>
                )}
              </>
            )}
          </li>
        ))}
      </ol>
      {includeJsonLd ? (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
        />
      ) : null}
    </nav>
  );
};

BreadcrumbsInline.displayName = 'BreadcrumbsInline';
